import { backOff } from "exponential-backoff";
import { OmmuseClient } from "../proto/api_grpc_web_pb";
import { ConvertFolderToProjectRequest, ConvertFolderToProjectResponse, CreateProjectRequest, CreateProjectResponse, DownloadProjectOrFolderRequest, DownloadProjectOrFolderResponse, GetProjectFilesRequest, GetProjectFilesResponse, GetProjectRequest, GetProjectResponse, LinkFoldersToProjectRequest, LinkFoldersToProjectResponse, PostProjectFilesBatchRequest, PostProjectFilesBatchResponse, Project, ProjectFile, Track, UpdateProjectFilesRequest, UpdateProjectFilesResponse, UpdateProjectRequest, UpdateProjectResponse } from "../proto/api_pb";
import { Collaborator } from "../proto/api_pb";
import { GetProjectsRequest } from "../proto/api_pb";
import { FileState, GetProjectsResponse } from "../proto/api_pb";
import { RpcError } from "grpc-web";

export const getAllProjects = async (
    token: string,
    userId: string,
    queryValue: string,
    selectedCollaborators: string[] | undefined,
    enterpriseId: string,
    page: number,
    role: number,
    // start updated 27/10
    sortBy?: string,
    sortOrder?: string
    // end
): Promise<GetProjectsResponse | RpcError | undefined> => {
    try {
        const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
        const request = new GetProjectsRequest();
        request.setQueryValue(queryValue);
        request.setPage(page);
        request.setLimit(8);
        request.setEnterpriseId(enterpriseId)
        request.setRole(role)
        request.setUserId(userId)
        if (selectedCollaborators && selectedCollaborators?.length > 0)
            request.setUserNamesList(selectedCollaborators);
        // start updated on 27/10
        if (sortBy) request.setSortBy(sortBy);
        if (sortOrder) request.setSortOrder(sortOrder);
        // end 
        const metadata = {
            Authorization: `${token}`,
            "Access-Control-Allow-Origin": "*",
        };
        return await backOff(
            () =>
                new Promise<GetProjectsResponse | RpcError>((resolve, reject) => {
                    ommuseClient.getProjects(request, metadata, function (err, response) {
                        if (err) {
                            reject(err);
                        } else {
                            resolve(response);
                        }
                    });
                }),
            {
                numOfAttempts: 3,
                jitter: "full",
            }
        );
    } catch (e) {
        console.log(e);
    }
};

export const addProject = async (
    token: string,
    userId: string,
    name: string,
    description: string,
    collaborators: Collaborator[],
    photo: string,
    enterpriseId: string
): Promise<CreateProjectResponse | RpcError | undefined> => {
    try {
        const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
        const request = new CreateProjectRequest();
        request.setOwnerId(userId);
        request.setName(name);
        request.setDescription(description);
        request.setProjectState(FileState.FILE_STATE_ACTIVE);
        request.setCollaboratorsList(collaborators);
        request.setArtworkUrl(photo);
        request.setEnterpriseId(enterpriseId)
        const metadata = {
            Authorization: `${token}`,
            "Access-Control-Allow-Origin": "*",
        };
        return await backOff(
            () =>
                new Promise<CreateProjectResponse | RpcError>((resolve, reject) => {
                    ommuseClient.createProject(
                        request,
                        metadata,
                        function (err, response) {
                            if (err) {
                                reject(err);
                            } else {
                                resolve(response);
                            }
                        }
                    );
                }),
            {
                numOfAttempts: 3,
                jitter: "full",
            }
        );
    } catch (e) {
        console.log(e);
    }
};



export const updateProject = async (
    token: string,
    project: Project,
): Promise<UpdateProjectResponse | RpcError | undefined> => {
    try {
        const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
        const request = new UpdateProjectRequest();
        request.setProject(project)
        const metadata = {
            Authorization: `${token}`,
            "Access-Control-Allow-Origin": "*",
        };
        return await backOff(
            () =>
                new Promise<UpdateProjectResponse | RpcError>((resolve, reject) => {
                    ommuseClient.updateProject(
                        request,
                        metadata,
                        function (err, response) {
                            if (err) {
                                reject(err);
                            } else {
                                resolve(response);
                            }
                        }
                    );
                }),
            {
                numOfAttempts: 3,
                jitter: "full",
            }
        );
    } catch (e) {
        console.log(e);
    }
};

export const postProjectFiles = async (
    token: string,
    projectFiles: ProjectFile[]
): Promise<PostProjectFilesBatchResponse | RpcError | undefined> => {
    try {
        const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
        const request = new PostProjectFilesBatchRequest();
        request.setProjectFilesList(projectFiles)
        const metadata = {
            Authorization: `${token}`,
            "Access-Control-Allow-Origin": "*",
        };
        return await backOff(
            () =>
                new Promise<PostProjectFilesBatchResponse | RpcError>((resolve, reject) => {
                    ommuseClient.postProjectFilesBatch(
                        request,
                        metadata,
                        function (err, response) {
                            if (err) {
                                reject(err);
                            } else {
                                resolve(response);
                            }
                        }
                    );
                }),
            {
                numOfAttempts: 3,
                jitter: "full",
            }
        );
    } catch (e) {
        console.log(e);
    }
};


export const getAllProjectFiles = async (
    token: string,
    projectId: string,
    query: string,
    filter: string,
    enterpriseId: string,
    page: number,
    limit: number,
    isGetAllFiles: boolean,
    filesSortBy: 0 | 1 | 2,
    filesSortingIsAscending: boolean,
    folderId: string | undefined,
): Promise<GetProjectFilesResponse | RpcError | undefined> => {
    try {
        const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
        const request = new GetProjectFilesRequest();
        request.setProjectId(projectId)
        request.setQuery(query)
        request.setFilter(filter)
        request.setPage(page)
        request.setLimit(limit)
        request.setEnterpriseId(enterpriseId)
        request.setGetAllFiles(isGetAllFiles)
        request.setSortby(filesSortBy)
        request.setIsAscending(filesSortingIsAscending)
        if (folderId) request.setFolderId(folderId)
        const metadata = {
            Authorization: `${token}`,
            "Access-Control-Allow-Origin": "*",
        };
        return await backOff(
            () =>
                new Promise<GetProjectFilesResponse | RpcError>((resolve, reject) => {
                    ommuseClient.getProjectFiles(
                        request,
                        metadata,
                        function (err, response) {
                            if (err) {
                                reject(err);
                            } else {
                                resolve(response);
                            }
                        }
                    );
                }),
            {
                numOfAttempts: 3,
                jitter: "full",
            }
        );
    } catch (e) {
        console.log(e);
    }
};

export const getProject = async (
    token: string,
    projectId: string,
    enterpriseId: string,
): Promise<GetProjectResponse | RpcError | undefined> => {
    try {
        const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
        const request = new GetProjectRequest();
        request.setProjectId(projectId)
        request.setEnterpriseId(enterpriseId)
        const metadata = {
            Authorization: `${token}`,
            "Access-Control-Allow-Origin": "*",
        };
        return await backOff(
            () =>
                new Promise<GetProjectResponse | RpcError>((resolve, reject) => {
                    ommuseClient.getProject(
                        request,
                        metadata,
                        function (err, response) {
                            if (err) {
                                reject(err);
                            } else {
                                resolve(response);
                            }
                        }
                    );
                }),
            {
                numOfAttempts: 3,
                jitter: "full",
            }
        );
    } catch (e) {
        console.log(e);
    }
};

export const updateProjectFiles = async (
    token: string,
    projectFiles: ProjectFile[],
    tracksToUpdate?: Track[]
): Promise<UpdateProjectFilesResponse | RpcError | undefined> => {
    try {
        const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
        const request = new UpdateProjectFilesRequest();
        request.setProjectFilesList(projectFiles)
        if (tracksToUpdate) request.setTracksList(tracksToUpdate)
        const metadata = {
            Authorization: `${token}`,
            "Access-Control-Allow-Origin": "*",
        };
        return await backOff(
            () =>
                new Promise<UpdateProjectFilesResponse | RpcError>((resolve, reject) => {
                    ommuseClient.updateProjectFiles(
                        request,
                        metadata,
                        function (err, response) {
                            if (err) {
                                reject(err);
                            } else {
                                resolve(response);
                            }
                        }
                    );
                }),
            {
                numOfAttempts: 3,
                jitter: "full",
            }
        );
    } catch (e) {
        console.log(e);
    }
};

export const linkFolderToProjectOrFolder = async (
    token: string,
    projectId: string,
    folderId: string[],
    parentFolderId: string,
    userId: string,
    enterpriseId: string
): Promise<LinkFoldersToProjectResponse | RpcError | undefined> => {
    try {
        const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
        const request = new LinkFoldersToProjectRequest();
        request.setProjectId(projectId)
        request.setFolderIdsList(folderId)
        request.setParentFolderId(parentFolderId)
        request.setUserId(userId)
        request.setEnterpriseId(enterpriseId)
        const metadata = {
            Authorization: `${token}`,
            "Access-Control-Allow-Origin": "*",
        };
        return await backOff(
            () =>
                new Promise<LinkFoldersToProjectResponse | RpcError>((resolve, reject) => {
                    ommuseClient.linkFoldersToProject(
                        request,
                        metadata,
                        function (err, response) {
                            if (err) {
                                reject(err);
                            } else {
                                resolve(response);
                            }
                        }
                    );
                }),
            {
                numOfAttempts: 3,
                jitter: "full",
            }
        );
    } catch (e) {
        console.log(e);
    }

}


export const downloadProject = async (
    token: string,
    enterpriseId: string,
    userId: string,
    projectId?: string,
    folderId?: string
): Promise<DownloadProjectOrFolderResponse | RpcError | undefined> => {
    try {
        const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
        const request = new DownloadProjectOrFolderRequest();
        request.setEnterpriseId(enterpriseId)
        if (projectId) request.setProjectId(projectId)
        if (folderId) request.setFolderId(folderId)
        request.setUserId(userId)
        const metadata = {
            Authorization: `${token}`,
            "Access-Control-Allow-Origin": "*",
        };
        return await backOff(
            () =>
                new Promise<DownloadProjectOrFolderResponse | RpcError>((resolve, reject) => {
                    ommuseClient.downloadProjectOrFolder(
                        request,
                        metadata,
                        function (err, response) {
                            if (err) {
                                reject(err);
                            } else {
                                resolve(response);
                            }
                        }
                    );
                }),
            {
                numOfAttempts: 3,
                jitter: "full",
            }
        );
    } catch (e) {
        console.log(e);
    }
};



export const handleConvertFolderToProject = async (
    token: string,
    enterpriseId: string,
    projectId: string,
    folderId: string
): Promise<ConvertFolderToProjectResponse | RpcError | undefined> => {
    try {
        const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
        const request = new ConvertFolderToProjectRequest();
        request.setEnterpriseId(enterpriseId)
        request.setFolderId(folderId)
        request.setProjectId(projectId)
        const metadata = {
            Authorization: `${token}`,
            "Access-Control-Allow-Origin": "*",
        };
        return await backOff(
            () =>
                new Promise<ConvertFolderToProjectResponse | RpcError>((resolve, reject) => {
                    ommuseClient.convertFolderToProject(
                        request,
                        metadata,
                        function (err, response) {
                            if (err) {
                                reject(err);
                            } else {
                                resolve(response);
                            }
                        }
                    );
                }),
            {
                numOfAttempts: 3,
                jitter: "full",
            }
        );
    } catch (e) {
        console.log(e);
    }
};
